
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Company } from '@/lib/types/trek-types';
import { Button } from '@/components/ui/button';
import EmptyState from '@/components/EmptyState';
import { formatNumber } from '@/lib/utils';
import { ApiFactory } from '@/api/api-factory';

const CompaniesSection = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedCompanies = async () => {
      try {
        setLoading(true);
        // Use the API Factory to get featured companies
        const result = await ApiFactory.getClient().getFeaturedCompanies(4);
        setCompanies(result);
      } catch (error) {
        console.error('Error fetching featured companies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedCompanies();
  }, []);

  if (loading) {
    return (
      <section className="py-16 md:py-24 bg-nature-50/50 dark:bg-nature-900/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-2 text-nature-800 dark:text-nature-200">Featured Companies</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Loading featured companies...
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="bg-white dark:bg-card border border-nature-200 dark:border-nature-700/40 h-64 rounded-xl animate-pulse"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (companies.length === 0) {
    return (
      <section className="py-16 md:py-24 bg-nature-50/50 dark:bg-nature-900/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-2 text-nature-800 dark:text-nature-200">Featured Companies</h2>
          </div>
          <EmptyState 
            title="No Featured Companies"
            description="There are no featured companies at the moment. Admin can set companies as featured from the admin panel."
            icon={<span className="text-3xl">🏢</span>}
          />
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-nature-50/50 dark:bg-nature-900/10">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-3xl font-bold mb-2 text-nature-800 dark:text-nature-200">Featured Companies</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Partner with the best trekking companies that offer unforgettable experiences led by experienced guides
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {companies.map((company) => (
            <Link
              key={company.id}
              to={`/company/${company.id}`}
              className="bg-white dark:bg-card border border-nature-200 dark:border-nature-700/40 hover:border-nature-400 dark:hover:border-nature-600/50 rounded-xl overflow-hidden transition-all hover:shadow-lg group company-card"
            >
              <div className="p-6 flex flex-col h-full">
                <div className="h-12 w-12 mb-4 bg-nature-50 dark:bg-nature-800/20 p-2 rounded-lg">
                  <img 
                    src={company.logo} 
                    alt={company.name} 
                    className="h-full w-full object-contain"
                  />
                </div>
                
                <h3 className="text-xl font-medium mb-2 group-hover:text-nature-600 dark:group-hover:text-nature-400 transition-colors">{company.name}</h3>
                
                <div className="flex items-center mb-3">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-4 h-4 ${i < Math.floor(company.rating) ? 'text-yellow-500' : 'text-gray-300 dark:text-gray-600'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="ml-1 text-sm text-muted-foreground">
                      ({formatNumber(company.reviewCount)} reviews)
                    </span>
                  </div>
                </div>
                
                <p className="text-muted-foreground text-sm mb-4 line-clamp-3 flex-grow">
                  {company.description.includes('<') ? (
                    <span dangerouslySetInnerHTML={{ __html: company.description }} />
                  ) : (
                    company.description
                  )}
                </p>
                
                <div className="flex items-center text-nature-600 dark:text-nature-400 mt-auto">
                  <span className="mr-1 text-sm font-medium">View </span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        <div className="text-center mt-10 md:mt-14">
          <Button asChild variant="outline" className="border-nature-300 dark:border-nature-700/50 hover:border-nature-500 hover:bg-nature-50 dark:hover:bg-nature-800/20">
            <Link to="/companies">
              View All Companies
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CompaniesSection;
